import { useIntl } from 'react-intl'
import { clsx } from 'clsx'

import { AddTagButton } from './AddTagButton'
import { EditorDropdown } from './EditorDropdown'
import { TagBadge } from './TagBadge'
import { TagEditor } from './TagEditor'
import type { Tag } from './tagging.types'
import { useTagging } from './useTagging'

interface EntityTagsInnerProps {
  addDataE2e: string
  className: string
  detailsDataE2e: string
  entityId: string
  readOnly?: boolean
  removeDataE2e: string
  tags: Tag[]
}

export const EntityTagsInner = (props: EntityTagsInnerProps) => {
  const {
    addDataE2e,
    className,
    detailsDataE2e,
    entityId,
    readOnly = false,
    removeDataE2e,
    tags,
  } = props
  const intl = useIntl()
  const tagging = useTagging()
  const isEditMode = !readOnly && tagging.mode === 'edit'
  const isDisabled = tagging.isAddingTag || tagging.isRemovingTag
  const hasTags = tags.length > 0
  const showAddButton = !readOnly && !isEditMode
  const showEmptyState = !hasTags && !isEditMode

  const handleEnterEditMode = () => {
    if (isDisabled || readOnly) {
      return
    }

    tagging.enterEditMode(entityId)
  }

  const handleRemoveTag = (tag: Tag) => {
    if (readOnly || tag.inherited) {
      return
    }

    tagging.removeTag([entityId], tag)
  }

  return (
    <div className={clsx('EntityTagsRow', className)} data-e2e={detailsDataE2e}>
      <span className="EntityTagsRow-label pe-2">
        {intl.formatMessage({ id: 'Tags' })}:
      </span>

      <div className="EntityTagsRow-tags">
        {tags.map(tag => (
          <TagBadge
            key={tag.id}
            color={tag.color}
            disableRemove={isDisabled}
            inherited={tag.inherited}
            inheritedFrom={tag.inheritedFrom}
            name={tag.name}
            onNameClick={
              isEditMode || readOnly ? undefined : handleEnterEditMode
            }
            onRemove={() => handleRemoveTag(tag)}
            removeDataE2e={removeDataE2e}
            showRemove={!readOnly}
          />
        ))}

        {isEditMode && (
          <div className="TagEditor-anchor">
            <TagEditor
              ariaLabel={intl.formatMessage({ id: 'Tags_add_tag_input' })}
              disabled={isDisabled}
              onExit={tagging.exitEditMode}
            />
            <EditorDropdown placement="start" />
          </div>
        )}

        {showAddButton && (
          <span data-e2e={addDataE2e}>
            <AddTagButton
              disabled={isDisabled}
              onClick={handleEnterEditMode}
              visible
            />
          </span>
        )}

        {showEmptyState && (
          <span className="EntityTagsRow-empty">
            {intl.formatMessage({ id: 'Tags_no_tags' })}
          </span>
        )}
      </div>
    </div>
  )
}
